import { useCallback } from 'react';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

/**
 * Returns a stable `fireEvent` callback for tracking user action events from the frontend.
 *
 * Usage:
 *   const fireEvent = useClientEvent();
 *   // In a button handler:
 *   fireEvent('ENV_NAMESPACE_EXPORTED', { period: '7d' });
 *
 * Events are written to `audit.audit_events` via POST /api/audit/client-event.
 * The call is fire-and-forget — errors are silently swallowed so the UI is never blocked.
 *
 * To make an event appear in the "Calls by Module" analytics chart, assign it to a module
 * via the admin Event Tracking page → Analytics Modules section.
 */
export function useClientEvent() {
  const getToken = useAccessToken();

  const fireEvent = useCallback(
    (eventName: string, payload?: Record<string, unknown>) => {
      // Fire-and-forget — never await in the caller
      (async () => {
        try {
          await fetchApi('/api/audit/client-event', getToken, {
            method: 'POST',
            body: JSON.stringify({
              eventName,
              pageUrl: window.location.pathname,
              httpMethod: 'CLIENT',
              ...(payload ? { eventPayload: payload } : {}),
            }),
          });
        } catch (err) {
          // Intentionally silent — analytics failures must never disrupt the user
          console.debug('[useClientEvent] event tracking failed:', eventName, err);
        }
      })();
    },
    // getToken can change reference without changing identity — exclude from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return fireEvent;
}
